import { useLoadoutStore } from '../store/loadoutSlice';
import {
  TRINKET_MAJOR_REGISTRY,
  TRINKET_MINOR_REGISTRY,
} from '../data/trinkets.config';
import {
  PERSO_SLOT_COUNT,
  type TrinketId,
  type TrinketLevel,
  type TrinketMajorDefinition,
  type TrinketMinorDefinition,
} from '../types/trinket.types';
import { liveLoadoutSource, type LoadoutSource } from './LoadoutSourceService';
import type {
  BuffDefinition,
  BuffHook,
  BuffModifier,
} from '../types/buff.types';
import type { ConfigParams } from '../types/effect.types';
import type { DungeonEntity } from '../entities/DungeonEntity';
import type { EffectContext } from '../types/spell.types';
import { DirectionEnum } from '../types/enums';
import { EFFECT_HANDLERS } from '../effect-system/effectHandlers';

/**
 * Smart layer around `useLoadoutStore` for trinkets. The store stays a neutral
 * setter; business logic (equip/unequip, anti-duplicate, run-start effects)
 * lives here.
 */

/**
 * Equips a major trinket (or `null` for the neutral fist). Minors are never
 * gated by the major — every minor stays equipped across major changes.
 */
export function equipMajor(modelId: TrinketId | null): void {
  const store = useLoadoutStore.getState();
  if (modelId !== null && store.getMajorLevel(modelId) === null) {
    return;
  }
  store.equipMajor(modelId);
}

/** Unequips the major (equivalent to `equipMajor(null)`). */
export function unequipMajor(): void {
  equipMajor(null);
}

/**
 * Equips a minor trinket on a slot 0..3. Preserves the slot's `spellId` — the
 * minor attaches to the spell already present in this slot, or stands alone if
 * there is none. Returns `false` for an invalid slot or an unowned minor.
 */
export function equipMinorInSlot(
  slotIndex: number,
  minorId: TrinketId,
): boolean {
  if (slotIndex < 0 || slotIndex >= PERSO_SLOT_COUNT) {
    return false;
  }
  const store = useLoadoutStore.getState();
  if (store.getMinorLevel(minorId) === null) {
    return false;
  }
  if (!TRINKET_MINOR_REGISTRY[minorId]) {
    return false;
  }

  // Anti-duplicate: if the minor is already equipped in another slot, move it.
  const equipped = store.equipped.slots;
  const existingSlot = equipped.findIndex((s) => s.minorId === minorId);
  if (existingSlot >= 0 && existingSlot !== slotIndex) {
    store.setSlot(existingSlot, { ...equipped[existingSlot], minorId: null });
  }

  const current = useLoadoutStore.getState().equipped.slots[slotIndex];
  store.setSlot(slotIndex, { ...current, minorId });
  return true;
}

/** Removes the minor from the given slot. Preserves the `spellId`. */
export function unequipMinorFromSlot(slotIndex: number): void {
  if (slotIndex < 0 || slotIndex >= PERSO_SLOT_COUNT) {
    return;
  }
  const store = useLoadoutStore.getState();
  const current = store.equipped.slots[slotIndex];
  store.setSlot(slotIndex, { ...current, minorId: null });
}

/**
 * Sets a spell in a perso slot. Preserves any attached minor. No tag
 * validation — every spell is freely equippable. Pass `null` to remove
 * the spell.
 */
export function setSpellInSlot(
  slotIndex: number,
  spellId: string | null,
): void {
  if (slotIndex < 0 || slotIndex >= PERSO_SLOT_COUNT) {
    return;
  }
  const store = useLoadoutStore.getState();
  if (spellId) {
    store.equipped.slots.forEach((s, i) => {
      if (i !== slotIndex && s.spellId === spellId) {
        store.setSlot(i, { ...s, spellId: null });
      }
    });
  }
  const current = store.equipped.slots[slotIndex];
  store.setSlot(slotIndex, { ...current, spellId });
}

/** Iterates over the currently equipped minors (slot by slot). */
export function iterateEquippedMinors(
  source: LoadoutSource = liveLoadoutSource(),
): {
  slotIndex: number;
  modelId: TrinketId;
  level: TrinketLevel;
  def: TrinketMinorDefinition;
  slot: { spellId: string | null; minorId: TrinketId | null };
}[] {
  const out: ReturnType<typeof iterateEquippedMinors> = [];
  source.slots.forEach((slot, slotIndex) => {
    if (!slot.minorId || slot.minorLevel === null) {
      return;
    }
    const def = TRINKET_MINOR_REGISTRY[slot.minorId];
    if (!def) {
      return;
    }
    out.push({
      slotIndex,
      modelId: slot.minorId,
      level: slot.minorLevel,
      def,
      slot: { spellId: slot.spellId, minorId: slot.minorId },
    });
  });
  return out;
}

/**
 * For each numeric param key `k` that has a sibling `${k}PerLevel`, returns a
 * new params object with `k` interpolated to `base + perLevel × (level - 1)`.
 * `*PerLevel` keys are stripped from the result. Does not recurse into nested
 * objects (e.g. the `step` param of addSpellEffectStep stays intact).
 */
export function scaleParamsByLevel(
  params: ConfigParams,
  level: number,
): ConfigParams {
  const perLevelKeys = Object.keys(params).filter((k) =>
    k.endsWith('PerLevel'),
  );
  if (perLevelKeys.length === 0) {
    return params;
  }
  const result: ConfigParams = {};
  const skipKeys = new Set(perLevelKeys);
  for (const [k, v] of Object.entries(params)) {
    if (skipKeys.has(k)) {
      continue;
    }
    const perLevelKey = `${k}PerLevel`;
    const perLevel = params[perLevelKey];
    if (typeof v === 'number' && typeof perLevel === 'number') {
      result[k] = v + perLevel * (level - 1);
    } else {
      result[k] = v;
    }
  }
  return result;
}

/**
 * Builds a runtime conditional-damage buff on the caster, idempotent by id.
 * Replaces the def in-place on re-apply so the multiplier stays current with
 * the trinket level (same pattern as applyTrinketStatScaling).
 */
function applyConditionalDamageBuff(ctx: EffectContext, p: ConfigParams): void {
  const id = p['id'] as string;
  const name = p['name'] as string;
  const multiplier = p['multiplier'] as number;
  const check = p['check'] as string;
  const conditionBuffId = p['conditionBuffId'] as string;
  const buffDef: BuffDefinition = {
    id,
    name,
    damageMultiplierConditions: [
      {
        multiplier,
        conditions: [{ check, params: { buffId: conditionBuffId } }],
      },
    ],
  };
  const existing = ctx.caster.getActiveBuffs().find((b) => b.def.id === id);
  if (existing) {
    existing.def = buffDef;
  } else {
    ctx.caster.addBuff({ def: buffDef, remainingTicks: undefined });
  }
}

/**
 * Builds a runtime shield-regen-on-low-hp buff on the caster, idempotent by
 * id. Replaces the def in-place on re-apply so the amount stays current with
 * the trinket level (same pattern as applyTrinketStatScaling).
 */
function applyShieldOnLowHpBuff(ctx: EffectContext, p: ConfigParams): void {
  const id = p['id'] as string;
  const name = p['name'] as string;
  const amount = p['amount'] as number;
  const hpThreshold = p['hpThreshold'] as number;
  const hook: BuffHook = {
    event: 'onDamageTaken',
    handler: 'healShield',
    params: { amount },
    conditions: [{ check: 'hpBelow', params: { threshold: hpThreshold } }],
  };
  const buffDef: BuffDefinition = { id, name, hooks: [hook] };
  const existing = ctx.caster.getActiveBuffs().find((b) => b.def.id === id);
  if (existing) {
    existing.def = buffDef;
  } else {
    ctx.caster.addBuff({ def: buffDef, remainingTicks: undefined });
  }
}

// Pure-stat trinket passives are not applied here: their modifiers live in
// effectsByLevel.modifiers / statScaling and are baked into the player's base
// stats at fresh-run creation (collectTrinketPassiveModifiers + bakeStatModifiers).
// applyTrinketEffects only runs the behavioral global steps. cf. docs/trinkets.md.
export function applyTrinketEffects(
  player: DungeonEntity,
  level: TrinketLevel,
  def: TrinketMajorDefinition | TrinketMinorDefinition,
): void {
  if (def.effectsByLevel) {
    for (let lvl = 1; lvl <= level; lvl++) {
      const effects = def.effectsByLevel[lvl];
      if (!effects?.global) {
        continue;
      }
      const ctx: EffectContext = {
        caster: player,
        direction: player.direction,
        affectedCells: [],
        trinketLevel: level,
      };
      for (const step of effects.global) {
        const handler = EFFECT_HANDLERS[step.handler];
        if (!handler) {
          if (step.handler === 'applyConditionalDamageBuff') {
            applyConditionalDamageBuff(
              ctx,
              scaleParamsByLevel(step.params, level),
            );
            continue;
          }
          if (step.handler === 'applyShieldOnLowHpBuff') {
            applyShieldOnLowHpBuff(ctx, scaleParamsByLevel(step.params, level));
            continue;
          }
          continue;
        }
        handler(ctx, scaleParamsByLevel(step.params, level));
      }
    }
  }
  if ('statScaling' in def && def.statScaling) {
    applyTrinketLifeSteal(player, def, level);
  }
}

// statScaling modifiers are baked into base stats at run start; only the
// life-steal part needs a live buff (read per-hit from active buffs). Keyed by
// modelId (unique per player) so re-derivation on resume is idempotent — the
// runtime buff is dropped on restore (absent from BUFF_REGISTRY) and re-built.
function applyTrinketLifeSteal(
  player: DungeonEntity,
  def: TrinketMinorDefinition | TrinketMajorDefinition,
  level: TrinketLevel,
): void {
  const scaling = 'statScaling' in def ? def.statScaling : undefined;
  if (scaling?.lifeStealPercentPerLevel === undefined) {
    return;
  }
  const id = `${def.id}-scaling`;
  const buffDef: BuffDefinition = {
    id,
    name: def.name,
    lifeStealPercent: scaling.lifeStealPercentPerLevel * level,
  };
  const existing = player.getActiveBuffs().find((b) => b.def.id === id);
  if (existing) {
    existing.def = buffDef;
  } else {
    player.addBuff({ def: buffDef, remainingTicks: undefined });
  }
}

/**
 * Aggregates the equipped trinkets' pure-stat passive modifiers:
 * `effectsByLevel.modifiers` (levels 1..level) plus `statScaling` modifiers
 * scaled by level. Baked into the run-start stats by
 * `RunStartProgressionService.buildRunStats`, on top of the per-level scaled base.
 */
export function collectTrinketPassiveModifiers(
  source: LoadoutSource = liveLoadoutSource(),
): BuffModifier[] {
  const equipped: Array<{
    level: TrinketLevel;
    def: TrinketMajorDefinition | TrinketMinorDefinition;
  }> = [];
  if (source.major !== null && source.majorLevel !== null) {
    const def = TRINKET_MAJOR_REGISTRY[source.major];
    if (def) {
      equipped.push({ level: source.majorLevel, def });
    }
  }
  for (const { level, def } of iterateEquippedMinors(source)) {
    equipped.push({ level, def });
  }

  const modifiers: BuffModifier[] = [];
  for (const { level, def } of equipped) {
    for (let lvl = 1; lvl <= level; lvl++) {
      for (const m of def.effectsByLevel?.[lvl]?.modifiers ?? []) {
        modifiers.push(m);
      }
    }
    if ('statScaling' in def && def.statScaling) {
      for (const m of def.statScaling.modifiers ?? []) {
        modifiers.push({
          stat: m.stat,
          ...(m.amountPerLevel !== undefined
            ? { amount: m.amountPerLevel * level }
            : {}),
          ...(m.percentPerLevel !== undefined
            ? { percent: m.percentPerLevel * level }
            : {}),
        });
      }
    }
  }
  return modifiers;
}

export function applyMinorOnAttachedSpellEffects(
  player: DungeonEntity,
  spellId: string,
  level: TrinketLevel,
  def: TrinketMinorDefinition,
): void {
  if (!def.effectsByLevel) {
    return;
  }
  for (let lvl = 1; lvl <= level; lvl++) {
    const effects = def.effectsByLevel[lvl];
    if (!effects?.onAttachedSpell) {
      continue;
    }
    const ctx: EffectContext = {
      caster: player,
      direction: DirectionEnum.TOP,
      affectedCells: [],
      targetSpellId: spellId,
      trinketLevel: level,
    };
    for (const step of effects.onAttachedSpell) {
      const handler = EFFECT_HANDLERS[step.handler];
      if (!handler) {
        continue;
      }
      handler(ctx, scaleParamsByLevel(step.params, level));
    }
  }
}
